import { api } from '@/lib/api-client'
import { isDemoDepartment } from '@/shared/constants/demo-data'

// ── Hydrated row shape returned by GET /entities/{id}/departments ──────────

export interface ApiDepartmentHod {
  id: string
  display_name: string
  email: string | null
  phone: string | null
}

export interface ApiDepartment {
  id: string
  entity_id: string
  name: string
  is_active: boolean
  hod: ApiDepartmentHod | null
  sites_count: number
  open_issues_count: number
  created_at: string
  updated_at: string
}

export interface ListDepartmentsResponse {
  entity_id: string
  entity_name: string
  departments: ApiDepartment[]
}

export function listDepartments(entityId: string): Promise<ListDepartmentsResponse> {
  return api
    .get<ListDepartmentsResponse>(`/entities/${entityId}/departments`)
    .then((d) => ({
      ...d,
      departments: (d.departments ?? []).filter((dep) => !isDemoDepartment(dep.id)),
    }))
}

// ── GET /departments/{id} — same hydrated shape + entity_name ──────────────

export interface ApiDepartmentDetail extends ApiDepartment {
  entity_name: string
}

export function getDepartment(departmentId: string): Promise<ApiDepartmentDetail> {
  return api.get<ApiDepartmentDetail>(`/departments/${departmentId}`)
}

// ── POST /entities/{id}/departments ────────────────────────────────────────

export interface CreateDepartmentRequest {
  name: string
  hod_id?: string
}

// The CREATE response is intentionally NOT hydrated (returns hod_id only).
// Refetch the list afterwards to get the hydrated `hod` object.
export interface CreateDepartmentResponse {
  id: string
  entity_id: string
  name: string
  hod_id: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export function createDepartment(
  entityId: string,
  body: CreateDepartmentRequest,
): Promise<CreateDepartmentResponse> {
  return api.post<CreateDepartmentResponse>(`/entities/${entityId}/departments`, body, {
    suppressErrorToast: true,
  })
}

// ── PATCH /departments/{id} ─────────────────────────────────────────────────

export interface UpdateDepartmentRequest {
  name?: string
  // null clears the HOD; omit the field to leave it unchanged.
  hod_id?: string | null
}

export interface UpdateDepartmentResponse {
  id: string
  entity_id: string
  name: string
  hod_id: string | null
  is_active: boolean
  updated_at: string
}

export function updateDepartment(
  departmentId: string,
  body: UpdateDepartmentRequest,
): Promise<UpdateDepartmentResponse> {
  return api.patch<UpdateDepartmentResponse>(`/departments/${departmentId}`, body, {
    suppressErrorToast: true,
  })
}

// ── DELETE /departments/{id} ────────────────────────────────────────────────

export interface DeactivateDepartmentResponse {
  id: string
  is_active: false
  updated_at?: string
}

// 409 conflict shape — backend returns just the count of active sites still
// referencing this department. No site list (FE can fetch sites if needed).
export interface DepartmentDeactivateBlocked {
  active_sites: number
}

export function deactivateDepartment(departmentId: string): Promise<DeactivateDepartmentResponse> {
  return api.delete<DeactivateDepartmentResponse>(`/departments/${departmentId}`, {
    suppressErrorToast: true,
  })
}

export interface ReactivateDepartmentResponse {
  id: string
  name?: string
  is_active: true
}

export function reactivateDepartment(
  departmentId: string,
): Promise<ReactivateDepartmentResponse> {
  return api.post<ReactivateDepartmentResponse>(
    `/departments/${departmentId}/reactivate`,
    undefined,
    {
      successMessage: 'Department reactivated.',
      suppressErrorToast: true,
    },
  )
}
